import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import Monogram from './layout/Monogram';
import Stamp from './layout/Stamp';
import { apiService } from '../services/apiService';

type TabKey = 'basic' | 'edu' | 'bio' | 'photos' | 'pricing';

interface Achievement {
  id: string;
  category: string;
  title: string;
  isPrimary: boolean;
  rank?: string | null;
  role?: string | null;
  year?: number | null;
  activityTier?: string | null;
}

interface Specialty {
  id: string;
  title: string;
}

interface Admission {
  id: string;
  university: string;
  status: string;
  isAttending: boolean;
  year?: number | null;
}

interface OfferingTemplateRow {
  id: string;
  category: string;
  activity: string;
  name: string;
  description: string | null;
  features: string[];
  sessionsCount: number;
}

interface MentorPkg {
  id: string;
  name: string;
  description?: string | null;
  features: string[];
  sessionsCount: number;
  price: number;
  isActive: boolean;
}

const EMPTY_PKG_FORM = {
  name: '',
  description: '',
  sessionsCount: 4,
  price: 0,
  features: '',
  isActive: true,
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const TIER_LABELS: Record<string, string> = {
  state: 'State ranking',
  championship: 'National/international championship',
  top300: 'Top 300',
  // legacy values
  national: 'National championship',
  international: 'International championship',
};

const admissionChipClass = (status: string) => {
  const s = status.toLowerCase();
  if (s.includes('reject')) return 'grid-chip reject';
  if (s.includes('wait')) return 'grid-chip waitlist';
  return 'grid-chip admit';
};

const MentorProfileEdit: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedTab, setSavedTab] = useState<TabKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('basic');

  const [profileId, setProfileId] = useState<string | null>(null);
  const [mentorType, setMentorType] = useState('student');
  const [isVerified, setIsVerified] = useState(false);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [admissions, setAdmissions] = useState<Admission[]>([]);
  const [packages, setPackages] = useState<MentorPkg[]>([]);
  const [pkgEditing, setPkgEditing] = useState<string | null>(null); // 'new' or a package id
  const [pkgSaving, setPkgSaving] = useState(false);
  const [pkgError, setPkgError] = useState<string | null>(null);
  const [pkgForm, setPkgForm] = useState({ ...EMPTY_PKG_FORM });
  const [tplActivity, setTplActivity] = useState<string>('');
  const [templates, setTemplates] = useState<OfferingTemplateRow[]>([]);
  const [activityOptions, setActivityOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [achForm, setAchForm] = useState({ category: '', activityTier: 'state', title: '', role: '' });
  const [achSaving, setAchSaving] = useState(false);
  const [achError, setAchError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getOfferingTemplates().then((r) => {
      if (r.success) setTemplates(r.templates);
    });
    apiService.getActivities().then((r) => {
      setActivityOptions(r.activities || []);
      if (r.activities?.length) {
        setAchForm((prev) => (prev.category ? prev : { ...prev, category: r.activities[0].name }));
      }
    });
  }, []);

  const [form, setForm] = useState({
    displayName: '',
    university: '',
    major: '',
    classYear: '',
    graduationYear: new Date().getFullYear(),
    occupation: '',
    almaMater: '',
    bio: '',
    mentoringExperience: '',
    profilePicture: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    availabilityNotes: '',
    pricePerSession: 0,
    isActive: true,
  });

  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/mentors/my-profile`, { headers: authHeaders });
      if (response.data.success && response.data.data) {
        const p = response.data.data;
        setProfileId(p.id);
        setMentorType(p.mentorType || 'student');
        setIsVerified(!!p.isVerified);
        setAchievements(p.achievements || []);
        setSpecialties(p.specialties || []);
        setAdmissions(p.admissions || []);
        setPackages(p.packages || []);
        setForm({
          displayName: p.displayName || '',
          university: p.university || '',
          major: p.major || '',
          classYear: p.classYear || '',
          graduationYear: p.graduationYear || new Date().getFullYear(),
          occupation: p.occupation || '',
          almaMater: p.almaMater || '',
          bio: p.bio || '',
          mentoringExperience: p.mentoringExperience || '',
          profilePicture: p.profilePicture || '',
          timezone: p.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          availabilityNotes: p.availabilityNotes || '',
          pricePerSession: p.pricePerSession || 0,
          isActive: p.isActive !== false,
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load your profile. Try refreshing.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field: string, value: string | number | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const saveFields = async (which: TabKey, payload: Record<string, any>) => {
    if (!profileId) return;
    setSaving(true);
    setError(null);
    try {
      const response = await axios.put(`${API_URL}/mentors/profile/${profileId}`, payload, {
        headers: authHeaders,
      });
      if (response.data.success) {
        setSavedTab(which);
        setTimeout(() => setSavedTab(null), 4000);
      } else {
        setError(response.data.error || 'Failed to save changes');
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    setError(null);
    setNewPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const savePhoto = async () => {
    if (!profileId) return;
    setSaving(true);
    setError(null);
    try {
      let url = form.profilePicture;
      if (newPhoto) {
        const fd = new FormData();
        fd.append('photo', newPhoto);
        const uploadRes = await axios.post(`${API_URL}/mentors/upload-photo`, fd, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        });
        if (uploadRes.data.success) url = uploadRes.data.url;
      }
      await axios.put(
        `${API_URL}/mentors/profile/${profileId}`,
        { profilePicture: url },
        { headers: authHeaders }
      );
      setForm((prev) => ({ ...prev, profilePicture: url }));
      setNewPhoto(null);
      setPhotoPreview(null);
      setSavedTab('photos');
      setTimeout(() => setSavedTab(null), 4000);
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = async () => {
    setNewPhoto(null);
    setPhotoPreview(null);
    if (form.profilePicture && profileId) {
      await saveFields('photos', { profilePicture: '' });
      setForm((prev) => ({ ...prev, profilePicture: '' }));
    }
  };

  const addAchievement = async () => {
    if (achSaving) return;
    if (!achForm.category || !achForm.title.trim()) {
      setAchError('Pick an activity and enter a title.');
      return;
    }
    setAchSaving(true);
    setAchError(null);
    try {
      const response = await axios.post(
        `${API_URL}/mentors/achievements`,
        {
          category: achForm.category,
          title: achForm.title.trim(),
          activityTier: achForm.activityTier,
          role: achForm.role.trim() || undefined,
        },
        { headers: authHeaders }
      );
      if (response.data.success) {
        setAchievements((prev) => [...prev, response.data.data]);
        setAchForm((prev) => ({ ...prev, title: '', role: '' }));
      }
    } catch (err: any) {
      console.error('Error adding achievement:', err);
      setAchError(err.response?.data?.error || 'Failed to add achievement.');
    } finally {
      setAchSaving(false);
    }
  };

  const deleteAchievement = async (achievementId: string) => {
    if (!window.confirm('Delete this achievement? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/mentors/achievements/${achievementId}`, {
        headers: authHeaders,
      });
      setAchievements((prev) => prev.filter((a) => a.id !== achievementId));
    } catch (err) {
      console.error('Error deleting achievement:', err);
      setError('Failed to delete achievement.');
    }
  };

  const openPkgEditor = (pkg?: MentorPkg) => {
    setPkgError(null);
    if (pkg) {
      setPkgEditing(pkg.id);
      setPkgForm({
        name: pkg.name,
        description: pkg.description || '',
        sessionsCount: pkg.sessionsCount,
        price: pkg.price,
        features: (pkg.features || []).join('\n'),
        isActive: pkg.isActive,
      });
    } else {
      setPkgEditing('new');
      setPkgForm({ ...EMPTY_PKG_FORM });
    }
    setTplActivity('');
  };

  const applyTemplate = (tpl: OfferingTemplateRow) => {
    const hourly = Number(form.pricePerSession) || 0;
    setPkgForm({
      name: tpl.name,
      description: tpl.description || '',
      sessionsCount: tpl.sessionsCount,
      price: hourly > 0 ? tpl.sessionsCount * hourly : 0,
      features: tpl.features.join('\n'),
      isActive: true,
    });
  };

  const savePackage = async () => {
    setPkgSaving(true);
    setPkgError(null);
    const payload = {
      name: pkgForm.name,
      description: pkgForm.description,
      sessionsCount: pkgForm.sessionsCount,
      price: pkgForm.price,
      features: pkgForm.features
        .split('\n')
        .map((f) => f.trim())
        .filter(Boolean),
      isActive: pkgForm.isActive,
    };
    try {
      const response =
        pkgEditing === 'new'
          ? await axios.post(`${API_URL}/mentors/packages`, payload, { headers: authHeaders })
          : await axios.put(`${API_URL}/mentors/packages/${pkgEditing}`, payload, {
              headers: authHeaders,
            });
      if (response.data.success) {
        const saved: MentorPkg = response.data.data;
        setPackages((prev) =>
          pkgEditing === 'new' ? [...prev, saved] : prev.map((p) => (p.id === saved.id ? saved : p))
        );
        setPkgEditing(null);
      }
    } catch (err: any) {
      setPkgError(err.response?.data?.error || 'Failed to save package. Please try again.');
    } finally {
      setPkgSaving(false);
    }
  };

  const deletePackage = async (pkgId: string) => {
    if (!window.confirm('Delete this offering? Families will no longer be able to book it.')) return;
    try {
      await axios.delete(`${API_URL}/mentors/packages/${pkgId}`, { headers: authHeaders });
      setPackages((prev) => prev.filter((p) => p.id !== pkgId));
      if (pkgEditing === pkgId) setPkgEditing(null);
    } catch (err) {
      console.error('Error deleting package:', err);
      setPkgError('Failed to delete package.');
    }
  };

  const isParent = mentorType === 'parent';
  const displayedPhoto = photoPreview || form.profilePicture;

  const saveBar = (which: TabKey, payload: Record<string, any>) => (
    <div className="save-bar">
      <span className="meta">{savedTab === which ? 'Saved just now' : ''}</span>
      <button
        className="btn btn-primary"
        disabled={saving}
        onClick={() => saveFields(which, payload)}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
            Edit profile
          </span>
          <h1 style={{ fontSize: 30 }}>Loading…</h1>
        </div>
      </div>
    );
  }

  if (!profileId) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap" style={{ maxWidth: 560 }}>
          <div className="card" style={{ padding: 36 }}>
            <h1 style={{ fontSize: 26, marginBottom: 10 }}>No mentor profile yet</h1>
            <p className="meta" style={{ marginBottom: 20 }}>
              Apply as a mentor first — once your application is in, you can edit everything here.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/become-a-mentor')}>
              Start the application
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      <div className="wrap">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <div>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
              Mentor · {form.displayName || user?.name}
            </span>
            <h1 style={{ fontSize: 30 }}>Edit your profile</h1>
            <p style={{ color: 'var(--ink-secondary)', marginTop: 8 }}>
              Everything here is what families see on your public page. Changes to credentials go
              back through verification before they show a stamp.
            </p>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/mentors/${profileId}`)}
          >
            Preview public profile
          </button>
        </div>

        {error && (
          <div className="note" style={{ marginTop: 16, borderColor: 'var(--cardinal)' }}>
            {error}
          </div>
        )}

        <div className="tabs" style={{ marginTop: 28 }}>
          <button className={tab === 'basic' ? 'on' : ''} onClick={() => setTab('basic')}>
            Basic info
          </button>
          <button className={tab === 'edu' ? 'on' : ''} onClick={() => setTab('edu')}>
            Education & achievements
          </button>
          <button className={tab === 'bio' ? 'on' : ''} onClick={() => setTab('bio')}>
            Specialties & bio
          </button>
          <button className={tab === 'photos' ? 'on' : ''} onClick={() => setTab('photos')}>
            Photos
          </button>
          <button className={tab === 'pricing' ? 'on' : ''} onClick={() => setTab('pricing')}>
            Pricing & settings
          </button>
        </div>

        {/* Tab 1: Basic info */}
        {tab === 'basic' && (
          <div className="card" style={{ marginTop: 24, maxWidth: 720 }}>
            <div className="row2">
              <div className="field">
                <label htmlFor="b-name">Full name</label>
                <input
                  id="b-name"
                  value={form.displayName}
                  onChange={(e) => set('displayName', e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="b-email">Email</label>
                <input id="b-email" value={user?.email || ''} disabled />
                <p className="help">Used for session notices only — never shown to families.</p>
              </div>
            </div>
            {isParent ? (
              <div className="row2">
                <div className="field">
                  <label htmlFor="b-occupation">Occupation</label>
                  <input
                    id="b-occupation"
                    value={form.occupation}
                    onChange={(e) => set('occupation', e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-alma">Alma mater (optional)</label>
                  <input
                    id="b-alma"
                    value={form.almaMater}
                    onChange={(e) => set('almaMater', e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div className="row3">
                <div className="field">
                  <label htmlFor="b-school">University</label>
                  <input
                    id="b-school"
                    value={form.university}
                    onChange={(e) => set('university', e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-major">Major</label>
                  <input
                    id="b-major"
                    value={form.major}
                    onChange={(e) => set('major', e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="b-year">Class year</label>
                  <input
                    id="b-year"
                    value={form.classYear}
                    onChange={(e) => set('classYear', e.target.value)}
                    placeholder="e.g., 2027"
                  />
                </div>
              </div>
            )}
            <div className="note">
              University and class year carry verification stamps. Editing them moves your profile
              to "re-verification" until ops re-checks the record — usually within 2 business days.
            </div>
            {saveBar(
              'basic',
              isParent
                ? {
                    displayName: form.displayName,
                    occupation: form.occupation,
                    almaMater: form.almaMater,
                  }
                : {
                    displayName: form.displayName,
                    university: form.university,
                    major: form.major,
                    classYear: form.classYear,
                    graduationYear: form.graduationYear,
                  }
            )}
          </div>
        )}

        {/* Tab 2: Education & achievements */}
        {tab === 'edu' && (
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 24,
                alignItems: 'start',
              }}
            >
              <div className="card">
                <h3 style={{ marginBottom: 4 }}>Admissions</h3>
                <p className="meta" style={{ marginBottom: 16 }}>
                  Your full application grid — admits, rejections, waitlists. Complete grids book
                  about twice as often.
                </p>
                {admissions.length === 0 ? (
                  <p className="meta">No schools on file yet.</p>
                ) : (
                  admissions.map((a) => (
                    <div className="list-row" key={a.id}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {a.university}{' '}
                          <span className={admissionChipClass(a.status)}>
                            {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                          </span>
                        </div>
                        <div className="meta">
                          {a.year ? `Class of ${a.year}` : 'Class year on file'}
                          {a.isAttending ? ' · enrolled' : ''}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <p className="meta" style={{ marginTop: 10 }}>
                  Schools are added and verified during application review — contact support to
                  update your grid.
                </p>
              </div>

              <div className="card">
                <h3 style={{ marginBottom: 4 }}>Achievements</h3>
                <p className="meta" style={{ marginBottom: 16 }}>
                  Each claim needs a source document. Verified claims show a green stamp with a
                  record ID.
                </p>
                {achievements.length === 0 ? (
                  <p className="meta">No achievements on file yet.</p>
                ) : (
                  achievements.map((a) => (
                    <div className="list-row" key={a.id} style={{ alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {a.title}{' '}
                          {a.isPrimary && <span className="tag solid">Primary</span>}
                        </div>
                        <div className="meta" style={{ margin: '4px 0 0' }}>
                          Category: {a.category}
                          {a.activityTier ? ` · ${TIER_LABELS[a.activityTier] || a.activityTier}` : ''}
                          {a.rank ? ` · ${a.rank}` : ''}
                          {a.role ? ` · Role: ${a.role}` : ''} ·{' '}
                          {isVerified ? (
                            <Stamp variant="verified" style={{ padding: '1px 8px' }}>
                              Verified
                            </Stamp>
                          ) : (
                            <Stamp variant="selfrep" style={{ padding: '1px 8px' }}>
                              Self-reported — verification pending
                            </Stamp>
                          )}
                        </div>
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => deleteAchievement(a.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
                <div
                  style={{
                    border: '1px solid var(--hairline)',
                    borderRadius: 8,
                    padding: 16,
                    marginTop: 14,
                  }}
                >
                  <h4 style={{ marginBottom: 12 }}>Add an achievement</h4>
                  <div className="row2">
                    <div className="field">
                      <label htmlFor="ach-activity">Activity</label>
                      <select
                        id="ach-activity"
                        value={achForm.category}
                        onChange={(e) => setAchForm((p) => ({ ...p, category: e.target.value }))}
                      >
                        {activityOptions.length === 0 && <option value="">Loading…</option>}
                        {activityOptions.map((a) => (
                          <option key={a.id} value={a.name}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="field">
                      <label htmlFor="ach-tier">Highest level reached</label>
                      <select
                        id="ach-tier"
                        value={achForm.activityTier}
                        onChange={(e) =>
                          setAchForm((p) => ({ ...p, activityTier: e.target.value }))
                        }
                      >
                        <option value="state">State ranking</option>
                        <option value="championship">National/international championship</option>
                        <option value="top300">Top 300</option>
                      </select>
                    </div>
                  </div>
                  <div className="row2">
                    <div className="field">
                      <label htmlFor="ach-title">Achievement</label>
                      <input
                        id="ach-title"
                        value={achForm.title}
                        maxLength={160}
                        onChange={(e) => setAchForm((p) => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. World Division Finalist, Think Award 1st place"
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="ach-role">Team role (optional)</label>
                      <input
                        id="ach-role"
                        value={achForm.role}
                        maxLength={80}
                        onChange={(e) => setAchForm((p) => ({ ...p, role: e.target.value }))}
                        placeholder="e.g. Programming lead"
                      />
                    </div>
                  </div>
                  {achError && (
                    <p className="meta" style={{ color: 'var(--cardinal)', marginBottom: 12 }}>
                      {achError}
                    </p>
                  )}
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={addAchievement}
                    disabled={achSaving}
                  >
                    {achSaving ? 'Adding…' : 'Add achievement'}
                  </button>
                </div>
                <p className="meta" style={{ marginTop: 10 }}>
                  New achievements start as self-reported and go through verification before they
                  show a stamp.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Specialties & bio */}
        {tab === 'bio' && (
          <div className="card" style={{ marginTop: 24, maxWidth: 720 }}>
            <div className="field">
              <label htmlFor="s-bio">Bio</label>
              <textarea
                id="s-bio"
                rows={6}
                value={form.bio}
                onChange={(e) => set('bio', e.target.value)}
              />
              <p className="help">
                Factual and specific reads best. No admission-probability claims — ops flags them.
              </p>
            </div>
            <div className="field">
              <label htmlFor="s-experience">Mentoring experience</label>
              <textarea
                id="s-experience"
                rows={5}
                value={form.mentoringExperience}
                onChange={(e) => set('mentoringExperience', e.target.value)}
                placeholder="Who have you mentored, for how long, and what did they achieve? e.g., 'Coached 20+ students through USACO, four reached Gold…'"
              />
              <p className="help">
                Shown as "{(form.displayName || 'Your name').split(' ')[0]}'s mentoring experience"
                on your profile. Leave blank to hide the section.
              </p>
            </div>
            <div className="field">
              <label>Specialties · up to 5</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {specialties.length === 0 ? (
                  <span className="meta">No specialties on file yet.</span>
                ) : (
                  specialties.map((s) => (
                    <span className="doc-chip" key={s.id}>
                      {s.title}
                    </span>
                  ))
                )}
              </div>
              <p className="help">
                Specialties are set during application review — contact support to change them.
              </p>
            </div>
            {saveBar('bio', { bio: form.bio, mentoringExperience: form.mentoringExperience })}
          </div>
        )}

        {/* Tab 4: Photos */}
        {tab === 'photos' && (
          <div className="card" style={{ marginTop: 24, maxWidth: 720 }}>
            <h3 style={{ marginBottom: 4 }}>Profile photo</h3>
            <p className="meta" style={{ marginBottom: 20 }}>
              1:1 crop, natural light, no filters. Until a photo passes review, your profile shows
              a serif monogram — plenty of booked mentors keep it that way.
            </p>
            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
              {displayedPhoto ? (
                <img
                  src={displayedPhoto}
                  alt="Profile"
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '1px solid var(--hairline)',
                  }}
                />
              ) : (
                <Monogram name={form.displayName} lg />
              )}
              <div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                    Upload photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoPick}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <button className="btn btn-ghost btn-sm" onClick={removePhoto}>
                    Delete
                  </button>
                </div>
                <p className="meta">
                  {newPhoto
                    ? `${newPhoto.name} selected — save to upload.`
                    : form.profilePicture
                      ? 'Photo on file.'
                      : 'No photo on file — monogram fallback active.'}
                </p>
              </div>
            </div>
            <div className="save-bar">
              <span className="meta">{savedTab === 'photos' ? 'Saved just now' : ''}</span>
              <button className="btn btn-primary" disabled={saving} onClick={savePhoto}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}

        {/* Tab 5: Pricing & settings */}
        {tab === 'pricing' && (
          <>
          <div className="card" style={{ marginTop: 24, maxWidth: 720 }}>
            <div className="row3">
              <div className="field">
                <label htmlFor="p-price">Price per session</label>
                <input
                  id="p-price"
                  type="number"
                  min={0}
                  value={form.pricePerSession}
                  onChange={(e) => set('pricePerSession', parseInt(e.target.value) || 0)}
                />
                <p className="help">
                  You keep 80% — ${Math.round(form.pricePerSession * 0.8)} per session at this
                  price.
                </p>
              </div>
              <div className="field">
                <label htmlFor="p-tz">Timezone</label>
                <input
                  id="p-tz"
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="p-notes">Availability notes</label>
                <input
                  id="p-notes"
                  value={form.availabilityNotes}
                  onChange={(e) => set('availabilityNotes', e.target.value)}
                  placeholder="e.g., weekends only during finals"
                />
              </div>
            </div>
            <div className="field">
              <label>Booking settings</label>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  marginTop: 4,
                  fontSize: 14,
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    fontWeight: 400,
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={!form.isActive}
                    onChange={(e) => set('isActive', !e.target.checked)}
                  />
                  Pause new bookings — keep profile hidden from browse
                </label>
              </div>
            </div>
            <div className="note">
              Your hourly rate is always bookable as a "Custom hourly" session. Most families
              don't know what to ask for — outcome-named offerings below convert far better.
            </div>
            {saveBar('pricing', {
              pricePerSession: form.pricePerSession,
              timezone: form.timezone,
              availabilityNotes: form.availabilityNotes,
              isActive: form.isActive,
            })}
          </div>

          {/* Custom packages */}
          <div className="card" style={{ marginTop: 20, maxWidth: 720 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <h3>Your offerings</h3>
              {pkgEditing === null && packages.length < 3 && (
                <button className="btn btn-secondary btn-sm" onClick={() => openPkgEditor()}>
                  + Add offering
                </button>
              )}
            </div>
            <p className="meta" style={{ marginBottom: 16 }}>
              Offer up to 3 result-oriented offerings named after the outcome — e.g. "Intro to
              FTC robotics", "Research proposal review", "Common App essay overhaul". Each is one
              or more 60-minute sessions. Families see these first, with your custom hourly rate
              below them.
            </p>

            {packages.length === 0 && pkgEditing === null && (
              <div className="note">
                No offerings yet — families currently only see "Custom hourly" at your rate.
                Named offerings help families who don't know what to ask for.
              </div>
            )}

            {packages.map((pkg) => (
              <div key={pkg.id} className="list-row">
                <div>
                  <b style={{ fontWeight: 600 }}>
                    {pkg.name} — ${pkg.price}
                  </b>
                  {!pkg.isActive && (
                    <span className="meta" style={{ marginLeft: 8 }}>
                      (hidden)
                    </span>
                  )}
                  <div className="meta">
                    {pkg.sessionsCount} session{pkg.sessionsCount !== 1 ? 's' : ''} · 60 min each
                    {pkg.description ? ` · ${pkg.description}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openPkgEditor(pkg)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--cardinal)' }}
                    onClick={() => deletePackage(pkg.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}

            {pkgEditing !== null && (
              <div
                style={{
                  border: '1px solid var(--hairline)',
                  borderRadius: 8,
                  padding: 16,
                  marginTop: 12,
                }}
              >
                <h4 style={{ marginBottom: 14 }}>
                  {pkgEditing === 'new' ? 'New offering' : 'Edit offering'}
                </h4>
                {pkgEditing === 'new' && templates.length > 0 && (
                  <div className="field">
                    <label htmlFor="pkg-template">
                      Start from a template{' '}
                      <span className="meta">(optional — pick the activity you mentor)</span>
                    </label>
                    <select
                      id="pkg-template"
                      value={tplActivity}
                      onChange={(e) => setTplActivity(e.target.value)}
                    >
                      <option value="">Choose an activity…</option>
                      {templates
                        .reduce<Array<{ category: string; activities: string[] }>>((acc, t) => {
                          let cat = acc.find((c) => c.category === t.category);
                          if (!cat) {
                            cat = { category: t.category, activities: [] };
                            acc.push(cat);
                          }
                          if (!cat.activities.includes(t.activity)) cat.activities.push(t.activity);
                          return acc;
                        }, [])
                        .map((cat) => (
                          <optgroup key={cat.category} label={cat.category}>
                            {cat.activities.map((activity) => (
                              <option key={activity} value={activity}>
                                {activity}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                    </select>
                    {tplActivity &&
                      templates
                        .filter((t) => t.activity === tplActivity)
                        .map((tpl) => (
                          <div key={tpl.id} className="list-row">
                            <div>
                              <b style={{ fontWeight: 600 }}>{tpl.name}</b>
                              <div className="meta">
                                {tpl.sessionsCount} session
                                {tpl.sessionsCount !== 1 ? 's' : ''} · {tpl.description}
                              </div>
                            </div>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ flexShrink: 0 }}
                              onClick={() => applyTemplate(tpl)}
                            >
                              Use
                            </button>
                          </div>
                        ))}
                    {tplActivity && (
                      <p className="help">
                        "Use" fills the form below with a suggested price of sessions × your
                        hourly rate — edit everything before saving.
                      </p>
                    )}
                  </div>
                )}
                <div className="field">
                  <label htmlFor="pkg-name">
                    Offering name <span className="meta">(name the result, not the hours)</span>
                  </label>
                  <input
                    id="pkg-name"
                    value={pkgForm.name}
                    maxLength={80}
                    onChange={(e) => setPkgForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Research proposal review · Intro to FTC robotics"
                  />
                </div>
                <div className="field">
                  <label htmlFor="pkg-desc">
                    One-line outcome <span className="meta">(what the family gets)</span>
                  </label>
                  <input
                    id="pkg-desc"
                    value={pkgForm.description}
                    maxLength={300}
                    onChange={(e) => setPkgForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="e.g. Get your robot design competition-ready before kickoff"
                  />
                </div>
                <div className="row3">
                  <div className="field">
                    <label htmlFor="pkg-sessions">Sessions</label>
                    <input
                      id="pkg-sessions"
                      type="number"
                      min={1}
                      max={20}
                      value={pkgForm.sessionsCount}
                      onChange={(e) =>
                        setPkgForm((p) => ({
                          ...p,
                          sessionsCount: parseInt(e.target.value) || 1,
                        }))
                      }
                    />
                    <p className="help">60 minutes each</p>
                  </div>
                  <div className="field">
                    <label htmlFor="pkg-price">Total price ($)</label>
                    <input
                      id="pkg-price"
                      type="number"
                      min={0}
                      value={pkgForm.price}
                      onChange={(e) =>
                        setPkgForm((p) => ({ ...p, price: parseInt(e.target.value) || 0 }))
                      }
                    />
                    <p className="help">
                      You keep 80% — ${Math.round(pkgForm.price * 0.8)}
                    </p>
                  </div>
                  <div className="field">
                    <label>Visibility</label>
                    <label
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        fontWeight: 400,
                        marginTop: 10,
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={pkgForm.isActive}
                        onChange={(e) =>
                          setPkgForm((p) => ({ ...p, isActive: e.target.checked }))
                        }
                      />
                      Visible to families
                    </label>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="pkg-features">
                    What's included <span className="meta">(one per line, up to 5)</span>
                  </label>
                  <textarea
                    id="pkg-features"
                    rows={4}
                    value={pkgForm.features}
                    onChange={(e) => setPkgForm((p) => ({ ...p, features: e.target.value }))}
                    placeholder={
                      'Review of current robot design\nWeekly build-season check-ins\nCompetition strategy session'
                    }
                  ></textarea>
                </div>
                {pkgError && (
                  <p className="meta" style={{ color: 'var(--cardinal)', marginBottom: 12 }}>
                    {pkgError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setPkgEditing(null);
                      setPkgError(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={pkgSaving || !pkgForm.name.trim() || pkgForm.price <= 0}
                    onClick={savePackage}
                  >
                    {pkgSaving ? 'Saving…' : 'Save offering'}
                  </button>
                </div>
              </div>
            )}
            {pkgError && pkgEditing === null && (
              <p className="meta" style={{ color: 'var(--cardinal)', marginTop: 12 }}>
                {pkgError}
              </p>
            )}
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MentorProfileEdit;
